/**
 * CreateApplicationModal Component
 *
 * Modal for creating a new application with metadata, organization settings,
 * and permission actions.
 *
 * Required fields:
 * - name: Display name for the application
 * - applicationCode: Unique code used in permissions (immutable after creation)
 * - actions: Array of supported actions for permission system
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Select } from '../../../components/ui/Select';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { ActionsBuilder } from './ActionsBuilder';
import { applicationCreateSchema } from '../../../lib/validators/application';
import type { ApplicationCreateDTO } from '../../../types/application.types';
import { useCreateApplication } from '../hooks/useCreateApplication';
import { useRoles } from '../../roles/hooks/useRoles';
import { RoleType } from '../../../types/role.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface CreateApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

/**
 * Generate application code from name
 * Converts "My Application Name" to "my-application-name"
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
};

/**
 * CreateApplicationModal component
 */
export const CreateApplicationModal: React.FC<CreateApplicationModalProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const [actions, setActions] = useState<string[]>(['read', 'write', 'delete']);
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');

  // Fetch all roles and filter for APPLICATION type
  const { data: allRoles = [], isLoading: isLoadingRoles } = useRoles(tenantId);
  const applicationRoles = useMemo(
    () => allRoles.filter((role) => role.type === RoleType.APPLICATION),
    [allRoles]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ApplicationCreateDTO>({
    resolver: zodResolver(applicationCreateSchema),
    defaultValues: {
      applicationName: '',
      applicationCode: '',
      actions: ['read', 'write', 'delete'],
      description: '',
      isActive: true,
      showInLauncher: true,
      menu: '',
      submenu: '',
    },
  });

  const watchName = watch('applicationName');

  // Auto-generate application code from name
  useEffect(() => {
    if (autoGenerateCode && watchName) {
      const generatedCode = generateCodeFromName(watchName);
      setValue('applicationCode', generatedCode);
    }
  }, [watchName, autoGenerateCode, setValue]);

  // Sync actions state with form
  useEffect(() => {
    setValue('actions', actions);
  }, [actions, setValue]);

  const createMutation = useCreateApplication({
    tenantId,
    onSuccess: (data) => {
      console.log('Application created:', data.applicationId);
      handleClose();
    },
    onError: (error) => {
      console.error('Failed to create application:', error);
    },
  });

  const handleClose = () => {
    reset();
    setActions(['read', 'write', 'delete']);
    setAutoGenerateCode(true);
    setSelectedRoleId('');
    onClose();
  };

  const onSubmit = async (data: ApplicationCreateDTO) => {
    // Ensure actions are included
    const submitData: ApplicationCreateDTO = {
      ...data,
      actions,
      ...(selectedRoleId && { roleId: selectedRoleId }),
    };
    createMutation.mutate(submitData);
  };

  const isLoading = createMutation.isPending;
  const error = createMutation.error;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Create Application</Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="error">
              {getErrorMessage(error)}
            </Alert>
          )}

          {/* Name Field */}
          <div>
            <Input
              label="Application Name"
              placeholder="My Application"
              {...register('applicationName')}
              error={errors.applicationName?.message}
              disabled={isLoading}
              required
            />
          </div>

          {/* Application Code Field */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Application Code <span className="text-red-500">*</span>
              </label>
              <label className="flex items-center text-xs text-gray-500">
                <input
                  type="checkbox"
                  checked={autoGenerateCode}
                  onChange={(e) => setAutoGenerateCode(e.target.checked)}
                  className="mr-1.5 h-3 w-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={isLoading}
                />
                Auto-generate from name
              </label>
            </div>
            <Input
              placeholder="my-application"
              {...register('applicationCode')}
              error={errors.applicationCode?.message}
              disabled={isLoading || autoGenerateCode}
            />
            <p className="mt-1 text-xs text-gray-500">
              Unique identifier used in permissions (e.g., "inventory:read"). Cannot be changed after creation.
            </p>
          </div>

          {/* Actions Field */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h4 className="text-sm font-medium text-gray-900 mb-1">
              Supported Actions <span className="text-red-500">*</span>
            </h4>
            <p className="text-xs text-gray-500 mb-3">
              Define what actions users can perform with this application. These will be used in role permissions.
            </p>
            <ActionsBuilder
              actions={actions}
              onChange={setActions}
              disabled={isLoading}
              error={errors.actions?.message}
            />
          </div>

          {/* Resource Role Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resource Role
            </label>
            {isLoadingRoles ? (
              <div className="flex items-center gap-2 py-2">
                <Spinner size="sm" />
                <span className="text-sm text-gray-500">Loading roles...</span>
              </div>
            ) : (
              <Select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                disabled={isLoading}
              >
                <option value="">None (no Ketrics resource access)</option>
                {applicationRoles.map((role) => (
                  <option key={role.roleId} value={role.roleId}>
                    {role.roleName} ({role.roleCode})
                  </option>
                ))}
              </Select>
            )}
            {!isLoadingRoles && applicationRoles.length === 0 && (
              <p className="text-sm text-amber-600 mt-1">
                No APPLICATION roles found. Create one in the Roles section first.
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Optional APPLICATION role that grants this app access to Ketrics resources (volumes, secrets, connections, etc.)
            </p>
          </div>

          {/* Description Field */}
          <div>
            <Textarea
              label="Description"
              placeholder="A brief description of your application"
              {...register('description')}
              error={errors.description?.message}
              disabled={isLoading}
              rows={3}
            />
          </div>

          {/* Menu Field */}
          <div>
            <Input
              label="Menu Category"
              placeholder="e.g., Analytics, CRM, Finance"
              {...register('menu')}
              error={errors.menu?.message}
              disabled={isLoading}
            />
            <p className="mt-1 text-sm text-gray-500">
              Top-level menu category for organizing applications
            </p>
          </div>

          {/* Submenu Field */}
          <div>
            <Input
              label="Submenu Category"
              placeholder="e.g., Reports, Dashboards"
              {...register('submenu')}
              error={errors.submenu?.message}
              disabled={isLoading}
            />
            <p className="mt-1 text-sm text-gray-500">
              Subcategory within the menu
            </p>
          </div>

          {/* Active Status Checkbox */}
          <div>
            <Checkbox
              label="Active"
              helperText="Controls whether this application can be launched and executed"
              {...register('isActive')}
              disabled={isLoading}
            />
          </div>

          {/* Show in Launcher Checkbox */}
          <div>
            <Checkbox
              label="Show in Launcher"
              helperText="Controls visibility in the application launcher. Disable for backend-only apps used for background jobs."
              {...register('showInLauncher')}
              disabled={isLoading}
            />
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
          disabled={isLoading}
        >
          Create Application
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
